<template>
    <div class="h-100">
        <b-row class="h-100 bg-grey">
            <b-col class="bg-white" cols="3">

                <div class="pt-4" v-if="item.applicationId">
                    <h2>Edit API credentials</h2>
                    <p>Review and edit your details.</p>
                    <p>View your Client ID and Client Secret here to authenticate with our API. More info is on the
                        documentation website here:</p>


                    <hr class="my-5">

                </div>
            </b-col>
            <b-col>
                <div class="d-flex flex-wrap align-content-between pb-100" v-if="item.applicationId">
                    <b-form class="pt-4 w-75">
                        <b-form-group
                                label="Name:"
                                label-size="sm"
                        >
                            <b-form-input
                                    placeholder="App name"
                                    v-model="item.name"
                            ></b-form-input>
                            <div>
                                <small>Name your app based on what role it will fulfill e.g. Property Importer</small>
                            </div>
                        </b-form-group>

                        <b-form-group
                                label="Description:"
                                label-size="sm"
                        >
                            <b-form-textarea
                                    placeholder="Describe what your app does"
                                    v-model="item.description"
                            ></b-form-textarea>
                            <div>
                                <small>Describe what your application will do</small>
                            </div>
                        </b-form-group>

                        <b-form-group
                                label="Client ID:"
                                label-size="sm"
                        >
                            <b-form-input
                                    :value="item.auth0.client_id"
                                    readonly
                            ></b-form-input>
                        </b-form-group>

                        <b-form-group
                                label="Client Secret:"
                                label-size="sm"
                        >
                            <b-form-input
                                    :value="item.auth0.client_secret"
                                    readonly
                            ></b-form-input>
                        </b-form-group>
                        <h4>IP Access</h4>
                        <hr>
                        <b-alert show>
                            This is a whitelist of IP addresses or IP Ranges that this set of credentials
                        can access the API from.
                        </b-alert>
                        <b-input-group v-for="ip in ips" class="mb-2">
                            <b-form-input type="text" :value="ip.ip" ></b-form-input>

                            <b-input-group-append>
                                <b-button variant="primary" @click="updateIp(ip)">
                                    <font-awesome-icon
                                            :icon="['fas', 'save']">
                                    </font-awesome-icon>
                                </b-button>
                                <b-button variant="danger" @click="deleteIp(ip)">
                                    <font-awesome-icon
                                            :icon="['far', 'times']">
                                    </font-awesome-icon>
                                </b-button>
                            </b-input-group-append>
                        </b-input-group>
                        <b-input-group >
                            <b-form-input type="text" v-model="ip" placeholder="Enter an IP Address to whitelist"
                            ></b-form-input>

                            <b-input-group-append>
                                <b-button variant="primary" @click="createIp(ip)">
                                    <font-awesome-icon
                                            :icon="['fal', 'plus']">
                                    </font-awesome-icon>
                                </b-button>
                            </b-input-group-append>
                        </b-input-group>
                    </b-form>

                </div>

            </b-col>
            <b-col cols="2">
                <div class="mt-4 d-flex justify-content-end">
                    <b-btn :to="{ name: 'machine-apps' }"
                           class="mb-3"
                           size="sm"
                           variant="primary">
                        Back to your apis
                        <font-awesome-icon
                                :icon="['far', 'arrow-right']"
                                class="ml-2">
                        </font-awesome-icon>
                    </b-btn>
                </div>
            </b-col>
            <vue-element-loading :active="loading"/>
        </b-row>
        <div class="form-footer fixed-bottom">
            <b-btn @click="update"
                   class="ml-auto"
                   variant="primary">
                Save
            </b-btn>
        </div>
    </div>
</template>

<script>
import MachineApplications from '@/services/Apps/MachineApplications';
import ErrorMixin from '@/components/Notifications/ErrorMixin';

export default {
  name: 'View-App',
  mixins: [ErrorMixin],
  data() {
    return {
      item: [],
      ip: '',
      loading: false,
    };
  },
  computed: {
    id() {
      return this.$route.params.id;
    },
    ips() {
      if (!this.item) {
        return [];
      }

      return this.item.ips;
    },
  },
  async mounted() {
    await this.fetchItem();
  },
  methods: {
    async fetchItem() {
      this.loading = true;

      try {
        const { data } = await MachineApplications.get(this.id);
        this.item = data.data;
      } catch (e) {
        this.$notify({
          duration: 5000,
          title: 'Unable to fetch your app',
          text: 'Unable to get your app at this time. Please try again later.',
          type: 'error',
        });
      } finally {
        this.loading = false;
      }
    },
    async update() {
      this.loading = true;

      const { name, description } = this.item;

      const params = {
        name,
        description,
      };

      try {
        await MachineApplications.update(this.id, params);
        await this.fetchItem();

        this.$notify({
          duration: 5000,
          title: 'App updated',
          text: 'These changes will take affect within the next 5 minutes',
          type: 'success',
        });
      } catch (e) {
        this.$notify({
          duration: 5000,
          title: 'Unable to update your app',
          text: 'Unable to get your app at this time. Please try again later.',
          type: 'error',
        });
      } finally {
        this.loading = false;
      }
    },
    async createIp(ip) {

      if (ip.length === 0 || !ip) {
        this.$notify({
          type: 'error',
          title: 'Please enter an IP Address',
          duration: 5000,
        });
        return false;
      }

      this.loading = true;

      try {
        await MachineApplications.createWhitelistIp(this.id, ip);
        await this.fetchItem();
        this.ip = '';

        this.$notify({
          duration: 5000,
          title: 'IP is now whitelisted',
          text: 'These changes will take affect within the next 5 minutes',
          type: 'success',
        });
      } catch (e) {
        this.showValidationError(e);
      } finally {
        this.loading = false;
      }
    },
    async updateIp(ip) {

      if (ip.length === 0 || !ip) {
        this.$notify({
          type: 'error',
          title: 'Please enter an IP Address',
          duration: 5000,
        });
        return false;
      }

      this.loading = true;

      try {
        await MachineApplications.updateWhitelistIp(this.id, ip);
        await this.fetchItem();

        this.$notify({
          duration: 5000,
          title: 'IP has been updated',
          text: 'These changes will take affect within the next 5 minutes',
          type: 'success',
        });
      } catch (e) {
        this.showValidationError(e);
      } finally {
        this.loading = false;
      }
    },
    async deleteIp(ip) {
      if (ip.length === 0 || !ip) {
        this.$notify({
          type: 'error',
          title: 'Please enter an IP Address',
          duration: 5000,
        });
        return false;
      }

      this.loading = true;

      try {
        await MachineApplications.deleteWhitelistIp(this.id, ip.applicationIpId);
        await this.fetchItem();

        this.$notify({
          duration: 5000,
          title: 'IP was deleted from this app',
          type: 'success',
        });
      } catch (e) {
        this.$notify({
          duration: 5000,
          title: 'Unable to update your app',
          text: 'Unable to get your app at this time. Please try again later.',
          type: 'error',
        });
      } finally {
        this.loading = false;
      }
    }

  },
};
</script>

<style scoped>
</style>
